import env from 'env/env';
import mapValues from 'lodash/mapValues';
import { rootPath } from 'utils/root-path';
import { SORT_URL_PURCHASE_FREQUENCY } from 'redux/modules/favourites-experience/constants';

// Constants file for internal app URLs, simple key/value
//
// Usage:
//   import urls from 'constants/urls';
//   ...
//   <AnchorLink to={urls.favourites}>

/** This is the same as URLs, but without the `rootPath` */
export const rawUrls = {
  authSuccess: 'auth-success',
  bookCollectionSlot: 'bookslot/collection/selectcollectionservice',
  bookDeliverySlot: 'bookslot/delivery',
  bookEntertainingCollectionSlot: 'bookslot/collection/entertaining',
  bookEntertainingCollectionChristmasSlot: 'bookslot/collection/entertaining/christmas',
  bookGroceriesAndEntertainingCollectionSlot: 'bookslot/collection/groceriesandentertaining',
  bookGroceriesCollectionSlot: 'bookslot/collection/groceries',
  bookingASlotInfo: 'help-information/customer-service/booking-a-slot-and-placing-an-order',
  bookslot: 'bookslot',
  bookslotCollection: 'bookslot/collection',
  boughtInStore: 'bought-in-store',
  browse: 'shop/browse',
  checkout: 'checkout',
  blockTraFraudUser: 'tra-check-block',
  christmasHomePage: 'christmas',
  citrusAdsBuylistPage: 'shop/buylist',
  citrusAdsPreview: 'bannerx',
  coronation: 'shop/browse/groceries/the_kings_coronation',
  covidfaqs: 'help-information/customer-service/coronavirus',
  customerService: 'help-information/:path*',
  customerServiceContactUsPage: 'help-information/customer-service/contact-us',
  customerServiceHomePage: 'help-information/customer-service',
  customerServiceCoronavirus: 'help-information/customer-service/coronavirus',
  customerServiceShopPage: 'help-information/customer-service/shopping-in-store',
  deleteAddress: 'my-details/addresses/delete',
  deliveryPassActive: 'myaccount/delivery-pass',
  deliveryPassAvailable: 'delivery-pass',
  easter: 'shop/browse/groceries/easter',
  entertainingChristmas: 'shop/browse/entertaining/christmas_food_to_order',
  entertainingHome: 'shop/browse/entertaining',
  favourites: 'favourites',
  favouritesOnOffer: `favourites?onOffer&sortBy=${SORT_URL_PURCHASE_FREQUENCY}`,
  featuredOffers: 'shop/featured/offers',
  forgotPassword: 'forgot-password',
  forgotPasswordExpired: 'forgot-password/expired',
  forgotPasswordLocked: 'forgot-password/locked',
  forgotPasswordSuccess: 'forgot-password/success',
  groceriesChristmas: 'shop/browse/groceries/christmas',
  groceriesHome: 'shop/browse/groceries',
  inspiration: 'content/inspiration',
  interstitials: 'beforeYouGo',
  joinMyWaitrose: 'membership/join',
  linkMyWaitrose: 'my-waitrose/link-card',
  linkMyWaitroseSuccess: 'my-waitrose/link-card/welcome',
  lists: 'lists',
  login: 'login',
  logout: 'log-out',
  logoutError: 'log-out/error',
  marketingPreferences: 'myaccount/marketing-preferences',
  mealDealPromoPath: 'shop/featured/meal-deals',
  mealDealDineIn: 'shop/featured/offers/meal-deals',
  moreOfWhatYouLove: 'quick-shop/more-of-what-you-love',
  mothersDay: 'shop/browse/groceries/mothers_day',
  multiSearch: 'shop/multi-search',
  myAccount: 'myaccount',
  myDetailsPage: 'myaccount/details-and-addresses',
  myOrdersPage: 'myaccount/my-orders',
  myPartnershipCardAndDetails: 'my-partnership/card-and-details',
  myWaitroseOffers: 'shop/featured/offers/my-waitrose-offers',
  myWaitrosePage: 'mywaitrose',
  myWaitroseBecomeAMember: 'mywaitrose/become-a-member',
  myWaitroseVouchers: 'mywaitrose/vouchers',
  myWaitroseRewards: 'mywaitrose/little-treats',
  myWaitroseMigrationCompetitions: 'mywaitrose/competitions',
  myWaitroseMigrationBenefits: 'mywaitrose/benefits',
  myWaitroseMigrationCardAndDetails: 'mywaitrose/card-and-details',
  new: 'shop/browse/groceries/new',
  newspapers: 'shop/browse/groceries/kitchen_dining_and_home/newsagents/newspapers',
  offerDetails: 'shop/offers',
  offers: 'shop/browse/offers',
  orderConfirmation: 'order-confirmation',
  orderReplacementCard: 'my-waitrose/order-replacement-card',
  orderReplacementCardAddress: 'my-waitrose/order-replacement-card/address',
  orderReplacementCardSuccess: 'my-waitrose/order-replacement-card/success',
  paymentCards: 'myaccount/payment-cards',
  paymentConfirmationPage: 'payment-confirmation',
  paymentFormIframe: 'assets/formbuilder.html',
  privacyNotice: 'help-information/privacy-notice',
  quickShop: 'quick-shop',
  recipe: 'recipe/:path',
  recipes: 'recipes/*',
  registration: 'registration',
  registrationAccountDetails: 'registration/account-details',
  registrationSuccess: 'registration/success',
  replaceMyWaitroseCard: 'help-information/customer-service/forms/replacement-card',
  reselectCollectionService: 'bookslot/collection/reselect-service',
  resetPassword: 'reset-password',
  resetPasswordSuccess: 'reset-password/success',
  reschedulingProhibited: 'reschedulingprohibited',
  resolveOrderPayment: 'resolve-order-payment/:customerOrderId',
  rewards: 'rewards',
  rewardsJohnLewis: 'rewards/johnlewis',
  search: 'shop/search',
  serviceSelection: 'serviceselection',
  serviceSelectionChristmas: 'serviceselection/christmas',
  serviceSelectionEaster: 'serviceselection/easter',
  shopByList: 'shop-by-list',
  shopByListSuggestions: 'shop-by-list/suggestions',
  shopByListUpload: 'shop-by-list/upload',
  shopFromPrevious: 'myaccount/my-orders/order/shop-from-order/:customerOrderId',
  shopFromPreviousInterstitial: 'myaccount/my-orders/order/shop-from-order',
  shoppingLists: 'shopping-lists/:shoppingListName?/:shoppingListId',
  signIn: 'sign-in',
  summer: 'shop/browse/groceries/summer',
  trolleyPage: 'shop/trolley',
  valentines: 'shop/browse/groceries/valentines',
  viewOrder: 'myaccount/my-orders/order/:customerOrderId',
  waitroseMagazine: 'products/waitrose-and-partners-food-magazine/065244-33064-33065',
  waitroseCookies: 'help-information/website-cookies',
  welcomeToMyWaitroseLinkCard: 'my-waitrose/link-card/welcome',
  wineBrowse: 'shop/browse/groceries/beer_wine_and_spirits/wine',
  // ^ add internal URLs here, root path will be prepended
  //   e.g. 'favourites' => '/ecom/favourites'
};

const urls = mapValues(rawUrls, rootPath);

export const shopFromPreviousURL = (orderId: number | string) =>
  urls.shopFromPrevious.replace(':customerOrderId', orderId?.toString());

export const resolveOrderPaymentURL = (orderId: string) =>
  urls.resolveOrderPayment.replace(':customerOrderId', orderId?.toString());

export const viewOrderURL = (orderId: string) =>
  urls.viewOrder.replace(':customerOrderId', orderId?.toString());
export const printOrderURL = (orderId: string) => `${viewOrderURL(orderId)}?print`;

/** This is the same as cmdUrls, but without the `rootPath` */
export const rawCmsUrls = [
  'help-information/*',
  'content/*',
  'christmas/*',
  'inspiration/*',
  'seasonal/*',
  'health/*',
  'social-responsibility/*',
  'recipes/meal-plans',
];

// list of all AEM routes which are handled by a single fetcher/component
// if you page is a hybrid (only part AEM driven structure) you should not use this list
export const cmsUrls = rawCmsUrls.map(key => rootPath(key));

const authServer = `/api/token-client-${env.apiEnv}/v1/auth`;

const extraUrls = {
  addressModals: {
    add: '#add-address',
    edit: '#edit-address',
    choose: '#choose-address',
  },
  homepage: '/',
  termsAndConditions:
    '/content/waitrose/en/corporate_information_home/corporate_information/legal_notices/terms_and_conditions.html',
  myWaitroseWCS: '/content/waitrose/en/home/mywaitrose/mywaitrose_welcome.html',
  branchFinder: '/find-a-store/',
  tokenClient: authServer,
  tokenClientRegistration: `${authServer}?prompt=login&screen_hint=signup&redirect=${urls.authSuccess}`,
  // ^ add URLs not under '/ecom' here
};

const mergedUrls = Object.assign(urls, extraUrls) as typeof urls & typeof extraUrls;

export default mergedUrls;
